<template>
  <div
    class="all"
    :class="{ 'has-numbers-visible': hasNumbersVisible }"
    >
    <div
      id="map-all"
      class="all__map"
      ref="all"
      >
    </div>

    <div
      class="dropdown is-left is-up"
      :class="{ 'is-active': isDropdownActive }"
      >
      <div class="dropdown-trigger">
        <button
          class="button"
          aria-haspopup="true"
          aria-controls="map-settings"
          @click="isDropdownActive = !isDropdownActive"
          >
          <span>Warstwy na mapie</span>
        </button>
      </div>
      <div
        id="map-settings"
        class="dropdown-menu"
        role="menu"
        >
        <div class="dropdown-content">
          <div class="dropdown-item">
            <label class="checkbox">
              <input type="checkbox" v-model="hasNumbersVisible" @change="updateMap">
              <span>Pokaż numery terenów</span>
            </label>
          </div>
          <hr class="dropdown-divider">
          <div
            class="dropdown-item"
            v-for="(type, key) in mapTypes"
            :key="`type-${key}`"
            >
            <label class="checkbox">
              <input type="checkbox" :value="key" v-model="mapTypesSelected">
              <span>{{ type.label }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  mapGetters,
  mapState,
} from 'vuex';

import {
  hasProp,
} from '@/utils';
import {
  fetchData,
  saveData,
} from '@/api';

export default {
  data: () => ({
    isDropdownActive: false,
    mapTypesSelected: [],
    hasNumbersVisible: true,
    map: null,
    leaflet: {
      controls: {
        position: 'topleft',
        drawCircle: false,
        drawCircleMarker: false,
        drawMarker: false,
        drawRectangle: false,
        drawPolyline: false,
        cutPolygon: false,
      },
      url: 'https://api.mapbox.com/styles/v1/{id}/tiles/256/{z}/{x}/{y}?access_token={accessToken}',
      urlParams: {
        id: 'emendelski/ck1f4st2m2z2z1cpk2fdctpeg',
        accessToken: 'pk.eyJ1IjoiZW1lbmRlbHNraSIsImEiOiJjazIzZjZhZ2gwamtoM2NtcXozOG1iZHF1In0.jaSfnYvGztiq0DHSDrTwiQ',
      },
    },
    uniqueShapes: {},
  }),
  mounted() {
    const { center, zoom } = this.settings.map;
    this.generateShapes();
    this.createLeafletMap(center, zoom);

    const lcPreferences = fetchData(this.$lc.preferencesName);

    if (lcPreferences) {
      this.$set(this, 'mapTypesSelected', lcPreferences);
    } else {
      this.$set(this, 'mapTypesSelected', Object.keys(this.mapTypes));
    }
  },
  computed: {
    ...mapState([
      'mapTypes',
      'list',
      'settings',
      'shapeStates',
    ]),
    ...mapGetters([
      'GET_MAP_COLOR',
    ]),
  },
  methods: {
    onMouse(state = 'active', group = {}, marker = {}, customIcon, customIconHover) {
      group.setStyle({ ...this.shapeStates[state] });

      if (state === 'active') {
        marker.setIcon(customIcon);
      } else {
        marker.setIcon(customIconHover);
      }
    },
    generateShapes() {
      Object.keys(this.list).forEach((key) => {
        const {
          id,
          name,
          type,
          map,
        } = this.list[key];

        const shapes = map?.shapes || [];
        const polygons = Array.from(shapes).map(shape => L.polygon(shape));

        const iconStyle = {
          className: 'my-icon',
          html: `<div class="my-label">${id}</div>`,
          iconSize: L.point(32, 32),
        };

        const customIcon = L.divIcon(iconStyle);
        const customIconHover = L.divIcon({
          ...iconStyle,
          className: 'my-icon my-icon--hover',
        });

        if (polygons.length) {
          const group = L.featureGroup(polygons);
          const marker = L.marker([], { icon: customIcon });

          group
            .on('click', () => this.$router.push({ path: `/list/${id}` }))
            .on('mouseover', () => this.onMouse('hover', group, marker, customIcon, customIconHover))
            .on('mouseout', () => this.onMouse('active', group, marker, customIcon, customIconHover));

          marker
            .on('click', () => this.$router.push({ path: `/list/${id}` }))
            .on('mouseover', () => this.onMouse('hover', group, marker, customIcon, customIconHover))
            .on('mouseout', () => this.onMouse('active', group, marker, customIcon, customIconHover));

          const values = {
            id,
            name,
            type: typeof type === 'undefined' ? 'default' : type,
            // leaflet objects
            polygons,
            marker,
            group,
          };

          const bounds = values.group.getBounds();

          if (bounds.isValid()) {
            const boundsCenter = bounds.getCenter();

            // set marker position to center of the group bounds
            values.marker.setLatLng(boundsCenter);

            // split into shapes based on their type
            if (hasProp(this.uniqueShapes, values.type)) {
              this.$set(this.uniqueShapes[values.type], key, values);
            } else {
              const typeObj = {};
              typeObj[key] = values;

              this.$set(this.uniqueShapes, values.type, typeObj);
            }
          } else {
            // eslint-disable-next-line
            console.warn('Error when gettings bounds center', {
              id,
              name,
              bounds,
              polygons,
            });
          }
        } else {
          // eslint-disable-next-line
          console.warn('No polygons', { id, name, polygons });
        }
      });
    },
    createLeafletMap(center, zoom) {
      if (this.map !== null) {
        this.map.off();
        this.map.remove();
      }

      this.map = L.map(this.$refs.all).setView(center, zoom);

      // Add background layer
      L.tileLayer(this.leaflet.url, this.leaflet.urlParams).addTo(this.map);
    },
    iterateOverTerritories(callback = () => {}) {
      if (typeof callback === 'function') {
        Object.keys(this.uniqueShapes).forEach((type) => {
          Object.keys(this.uniqueShapes[type]).forEach((territoryKey) => {
            const territory = this.uniqueShapes[type][territoryKey];
            const isActive = Array.from(this.mapTypesSelected).includes(territory?.type);

            callback(territory, isActive);
          });
        });
      }
    },
    updateMarkers(isActive, marker) {
      let markerOpacity = isActive ? 1 : 0;

      if (!this.hasNumbersVisible) {
        markerOpacity = 0;
      }

      if (markerOpacity === 0 && this.map.hasLayer(marker)) {
        this.map.removeLayer(marker);
      } else if (isActive) {
        this.map.addLayer(marker);
      }
    },
    updateMap() {
      saveData(this.mapTypesSelected, this.$lc.preferencesName);

      this.iterateOverTerritories(({ marker, group, type }, isActive) => {
        const color = this.GET_MAP_COLOR(type);

        // adjust markers
        this.updateMarkers(isActive, marker);

        if (!isActive && this.map.hasLayer(group)) {
          this.map.removeLayer(group);
        } else if (isActive) {
          group.setStyle({
            ...this.shapeStates.active,
            fillColor: color,
            color,
          });

          this.map.addLayer(group);
        }
      });
    },
  },
  watch: {
    mapTypesSelected: 'updateMap',
  },
};
</script>

<style lang="scss" scoped>
  .all {
    width: 100%;
    height: calc(100vh - 3.25rem);
    background-color: #ddd;
    position: relative;

    &__map {
      position: absolute;
      display: block;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
  }

  .dropdown {
    position: fixed;
    bottom: 2rem;
    left: 2rem;
    z-index: 2;
  }

  .checkbox {
    display: inline-flex;
    align-items: center;

    > input[type="checkbox"] {
      vertical-align: middle;
      margin-inline-end: .25rem;
    }
  }
</style>

<style lang="scss">
.has-numbers-visible .leaflet-marker-icon.my-icon {
  opacity: 1;
}

.my-icon {
  background-color: white;
  box-shadow: rgba(#000, .5) 0 0 .25;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: bold;
  opacity: 0;

  &--hover {
    box-shadow: rgba(#000, .5) 0 0 .25rem;
  }
}

.my-label {
  white-space: pre;
}
</style>
